import { pushGtmEvent } from '@/src/analytics/gtm'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { getGtmPageCountry } from '@/src/utils/analytics/gtm/getGtmPageCountry'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getUserAuthValues } from '@/src/utils/auth'
import { getUserSelectedCurrencyCode } from '@/src/utils/currency/getCurrencyCode'
import { getFormattedDate } from '@/src/utils/date'
import { getPlatformName } from '@/src/utils/deviceType'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'
import { parseMulticitySectors } from '@/src/utils/srp/searchUtils'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'

const useSrpGtmEvent = () => {
    const locale = useLocale()
    const { urlQueryObj } = useGetSearchParamsObj()

    const t = useTranslations('metadata')
    const deviceType = getDeviceType()
    const platform = getPlatformName(deviceType)?.toLowerCase()
    const isSignedIn = isUserSignedIn()
    const { email } = getUserAuthValues() || {}
    const userEmailPhone = isSignedIn ? `{"user_email":"${email || 'not_informed'}","user_phone":"not_informed"}` : null
    // useSearchResults owns the OW/RT-vs-multicity API routing and exposes the shared
    // airports map (jsons.ap) we use for city names below.
    const { jsons, isMulticity } = useSearchResults()

    // Resolve the search context per trip type. For multicity the journey is collapsed to
    // first-sector departure → last-sector arrival, using the first/last sector dates and
    // the total sector count. OW/RT keep their existing from/to/depart/return semantics.
    const { from, to, flightClass, departDate, returnDate, adults, childs, infants, item, tripType, sectorCount } =
        useMemo(() => {
            const {
                from: searchFrom = '',
                to: searchTo = '',
                class: flightClass,
                depart_date,
                return_date,
                adults,
                childs,
                infants
            } = urlQueryObj || {}

            const travelers = {
                adults: Number(adults),
                childs: Number(childs),
                infants: Number(infants)
            }

            if (isMulticity) {
                const { sectors } = parseMulticitySectors(urlQueryObj || {})
                const mcFrom = sectors[0]?.from ?? ''
                const mcTo = sectors[sectors.length - 1]?.to ?? ''

                return {
                    ...travelers,
                    from: mcFrom,
                    to: mcTo,
                    flightClass,
                    departDate: sectors[0]?.depart_date,
                    returnDate: sectors[sectors.length - 1]?.depart_date,
                    item: `${mcFrom}-${mcTo}-mc`,
                    tripType: 'multi_city_trip',
                    sectorCount: sectors.length
                }
            }

            return {
                ...travelers,
                from: searchFrom,
                to: searchTo,
                flightClass,
                departDate: depart_date,
                returnDate: return_date,
                item: `${searchFrom}-${searchTo}-${return_date ? 'rt' : 'ow'}`,
                tripType: return_date ? 'round_trip' : 'one_way_trip',
                sectorCount: return_date ? 2 : 1
            }
        }, [urlQueryObj, isMulticity])

    // City names come from the airports map (jsons.ap), which already reflects whichever
    // results query useSearchResults resolved for the current trip type.
    const ap = jsons?.ap
    const fromCity = ap?.[from]?.c || ''
    const toCity = ap?.[to]?.c || ''

    const pushEventToDataLayer = (payload: any) => {
        // TODO: When writing for Cleartrip, we can define a function here that creates the payload.
        // Based on it, for Cleartrip or Flyin we can have two functions and we will use that to get the payload.

        const finalPayload = {
            PageType: 'FlightResultPage',
            item,
            trip_type: tripType,
            source_airport_code: from?.toLowerCase(),
            destination_airport_code: to?.toLowerCase(),
            source_airport_city: fromCity,
            destination_airport_city: toCity,
            travelers_adult_count: adults,
            travelers_children_count: childs,
            travelers_infants_count: infants,
            travelers_total_count: adults + childs + infants,
            number_of_sectors: sectorCount,
            departing_departure_date: getFormattedDate(departDate, 'YYYY/MM/DD'),
            returning_departure_date: returnDate
                ? getFormattedDate(returnDate, 'YYYY/MM/DD')
                : 'no_returning_departure_date',
            flight_class: flightClass?.toLowerCase(),
            // city_id: '50257',
            product_category: 'flights',
            page_referrer: document.referrer || 'no_page_referrer',
            page_language: locale,
            page_location: window.location.href,
            page_sub_category: 'flights',
            page_path: window.location.pathname,
            page_domain: window.location.hostname,
            page_title: t('title'),
            page_country: getGtmPageCountry(window.location.hostname),
            page_currency: getUserSelectedCurrencyCode()?.toLowerCase() || '',
            user_email_phone: userEmailPhone,
            user_logged_in: isSignedIn ? 'yes' : 'no',
            is_new_flow: 'yes',
            platform,
            ...payload
        }

        pushGtmEvent(finalPayload)
    }

    return {
        pushEventToDataLayer
    }
}

export default useSrpGtmEvent
